import LegalPageLayout from './LegalPageLayout'

const content = `# Data Processing Addendum (DPA) — EasyAppIcon

**Effective date:** 20 Jan 2026

This Data Processing Addendum ("**DPA**") forms part of the Terms of Service between **EasyIO trading as "EasyAppIcon"** ("**EasyAppIcon**", "**Processor**", "**we**", "**us**") and the customer entity or person that uses the Services ("**Customer**", "**Controller**", "**you**").

This DPA applies to the extent EasyAppIcon processes **Personal Data** on behalf of Customer as part of providing the EasyAppIcon website, dashboard, and API (the "**Services**").

> **Order of precedence.** If there is a conflict between this DPA and the Terms of Service, this DPA governs **only** for the processing of Personal Data as described here. All other terms remain governed by the Terms of Service.

---

## 1) Definitions

1.1 **Applicable Data Protection Laws** means all laws and regulations applicable to the processing of Personal Data under this DPA, including (where applicable) the **EU GDPR**, **UK GDPR**, and the **Hong Kong Personal Data (Privacy) Ordinance (PDPO)**.

1.2 **Controller**, **Processor**, **Personal Data**, **Data Subject**, **Processing**, and **Supervisory Authority** have the meanings given in Applicable Data Protection Laws.

1.3 **Customer Content** means the images/files and related parameters uploaded or submitted by Customer (or Customer's end users) to the Services, including generated output files.

1.4 **Subprocessor** means a third party engaged by EasyAppIcon to process Personal Data on behalf of Customer.

1.5 **Security Incident** means a breach of security leading to the accidental or unlawful destruction, loss, alteration, unauthorized disclosure of, or access to Personal Data ("Personal Data Breach" under GDPR).

1.6 **Customer Personal Data** means Personal Data that EasyAppIcon processes **as a Processor on Customer's behalf** under this DPA, including Personal Data contained in Customer Content and any personal data submitted via the Services for processing (including associated metadata necessary to provide the processing, such as request timestamps and API key identifiers).

1.7 **Controller Data** means personal data that EasyAppIcon processes **as an independent Controller**, including (by way of example) account administration, billing, product analytics, fraud/abuse prevention, and general website analytics, which are governed by the Privacy Policy and Terms of Service rather than this DPA.

---

## 2) Scope of processing

2.1 **Roles.** Customer is the **Controller** of Customer Personal Data and EasyAppIcon acts as the **Processor** when processing Customer Personal Data on Customer's behalf.

2.2 **Customer instructions.** EasyAppIcon will process Personal Data only:
- to provide the Services in accordance with the Terms of Service and this DPA;
- on Customer's documented instructions (including API requests, configuration, and written instructions via support); and
- as required by applicable law (in which case we will notify Customer unless legally prohibited).

2.3 **Unlawful instructions.** If EasyAppIcon believes that Customer's instructions infringe Applicable Data Protection Laws, EasyAppIcon will inform Customer without undue delay and may suspend the relevant processing until the parties have agreed lawful instructions.

2.4 **Controller Data.** This DPA applies only to Customer Personal Data. It does not apply to Controller Data, which EasyAppIcon processes as an independent Controller and which is governed by the Privacy Policy and Terms of Service.

2.5 **Details of processing.** The subject matter, duration, nature, and purposes of processing, as well as categories of Customer Personal Data and Data Subjects, are set out in **Annex 1 (Processing Details)**.

---

## 3) Customer obligations

Customer represents and warrants that:
- it has a valid legal basis to collect and provide Personal Data to EasyAppIcon and to instruct EasyAppIcon to process it;
- it will provide any required notices to Data Subjects and obtain any required consents (e.g., where Customer uploads third-party images);
- its instructions will comply with Applicable Data Protection Laws; and
- it will not submit highly sensitive regulated data (e.g., PHI) unless EasyAppIcon has agreed in writing.

Customer is responsible for determining whether the Services are suitable for its use, including any required DPIAs, risk assessments, or transfer assessments.

---

## 4) Confidentiality

4.1 EasyAppIcon will ensure that persons authorized to process Customer Personal Data are bound by confidentiality obligations (contractual or statutory) and receive appropriate privacy/security training as relevant to their role.

---

## 5) Security measures

5.1 EasyAppIcon will implement and maintain appropriate technical and organizational measures to protect Customer Personal Data against Security Incidents, as described in **Annex 2 (Security Measures)**.

5.2 Customer acknowledges that security is a shared responsibility. Customer is responsible for:
- keeping API keys and credentials secure;
- configuring access controls and account permissions appropriately; and
- ensuring secure collection and upload of Customer Content.

---

## 6) Subprocessors

6.1 **General authorization.** Customer provides general authorization for EasyAppIcon to engage Subprocessors to process Customer Personal Data on Customer's behalf.

6.2 **Current Subprocessors.** As of the Effective Date, EasyAppIcon uses the Subprocessors listed in **Annex 3 (Subprocessors)**.

6.3 **Changes to Subprocessors.** EasyAppIcon may add or replace Subprocessors by updating Annex 3 and providing **at least fifteen (15) days' prior notice** (e.g., by website posting or dashboard notice). If Customer objects on reasonable data protection grounds, the parties will work in good faith to find a resolution (which may include Customer discontinuing the affected Service).

6.4 **Flow-down obligations.** EasyAppIcon will impose data protection obligations on Subprocessors that are no less protective than this DPA, including confidentiality and security requirements.

---

## 7) Assistance to Customer

7.1 **Data Subject requests.** If EasyAppIcon receives a request from a Data Subject relating to Customer Personal Data processed under this DPA, EasyAppIcon will (to the extent legally permitted) direct the Data Subject to Customer. Upon Customer's request, EasyAppIcon will provide reasonable assistance to help Customer respond.

7.2 **Security, DPIAs, consultations.** EasyAppIcon will provide reasonable assistance (taking into account the nature of processing and information available) with:
- security obligations,
- data protection impact assessments, and
- consultations with supervisory authorities,
as required by Applicable Data Protection Laws.

---

## 8) Security Incident notification

8.1 **Notification.** EasyAppIcon will notify Customer **without undue delay** after becoming aware of a Security Incident affecting Customer Personal Data processed under this DPA.

8.2 **Information.** To the extent available, the notification will include:
- a summary of what happened,
- categories and approximate number of impacted Data Subjects and records,
- likely consequences, and
- measures taken or proposed to address the incident.

8.3 **No admission.** Security Incident notifications are not an admission of fault or liability.

---

## 9) Return and deletion of Personal Data

9.1 **Deletion upon termination.** Upon termination of the Services (or upon Customer's written request where applicable), EasyAppIcon will delete or return Customer Personal Data in Customer Content in accordance with the Terms of Service and this DPA, unless retention is required by law.

9.2 **Routine deletion.** EasyAppIcon is designed to minimize retention of Customer Content (e.g., automated deletion **within twenty-four (24) hours** after output delivery by default), as described in the Privacy Policy and applicable product configuration. Where EasyAppIcon retains technical logs or security telemetry, it does so only for as long as necessary for security, abuse prevention, troubleshooting, and reliability.

9.3 **Backups and residuals.** Customer Personal Data may persist in encrypted backups for a limited period consistent with backup retention policies, after which it will be overwritten or deleted in the normal course.

---

## 10) Audits and compliance

10.1 **Information.** Upon reasonable request, EasyAppIcon will make available information necessary to demonstrate compliance with this DPA.

10.2 **Audits.** Where required by Applicable Data Protection Laws, Customer may conduct (or appoint an independent auditor to conduct) an audit:
- no more than once per 12 months (unless a Security Incident or regulator request justifies more),
- with reasonable advance notice,
- during normal business hours,
- subject to confidentiality obligations, and
- in a manner that does not unreasonably interfere with operations.

EasyAppIcon may satisfy audit requests by providing relevant third-party audit reports or certifications (if available) and/or responses to security questionnaires.

---

## 11) International data transfers

11.1 **Processing locations.** EasyAppIcon and its Subprocessors may process Customer Personal Data in the countries/regions listed in Annex 3 or otherwise disclosed in the Privacy Policy.

11.2 **EEA transfers (EU SCCs).** If Customer is subject to the GDPR and Customer Personal Data is transferred to a country without an adequacy decision, the parties agree that the **EU Standard Contractual Clauses (Controller-to-Processor, Module Two)** are incorporated by reference and apply to such transfers:
- Commission Implementing Decision (EU) 2021/914 (the "**EU SCCs**"): https://eur-lex.europa.eu/eli/dec_impl/2021/914/oj/eng

For purposes of the EU SCCs:
- Customer is the "data exporter" and EasyAppIcon is the "data importer";
- the Annexes to this DPA (Annex 1–3) complete the EU SCC appendices as applicable; and

**SCC elections.** The parties agree to the following elections for the EU SCCs (to the extent applicable):
- **Clause 7 (Docking clause):** Enabled.
- **Clause 11 (Redress):** Not selected.
- **Clause 13(a) Supervisory authority:** The supervisory authority of the EU Member State where the Customer (data exporter) is established. If the Customer is not established in the EEA, the parties designate the **Irish Data Protection Commission**.
- **Clause 17 (Governing law):** Ireland.
- **Clause 18 (Choice of forum and jurisdiction):** The courts of Ireland.

11.3 **UK transfers (UK Addendum).** If Customer is subject to the UK GDPR and the transfer is a restricted transfer, the parties agree that the **UK International Data Transfer Addendum to the EU SCCs** (the "**UK Addendum**") is incorporated by reference and applies:
- UK Addendum (ICO): https://ico.org.uk/media2/migrated/4019539/international-data-transfer-addendum.pdf

11.4 **Switzerland.** Where Swiss data protection law applies, the parties will interpret and adapt the EU SCCs as required by Swiss law and guidance (including references to the competent Swiss authority and applicable Swiss terminology).

11.5 **Supplementary measures.** The parties will implement supplementary technical and organizational measures where necessary to maintain an appropriate level of protection for Customer Personal Data, taking into account the nature of the transfer and the Services.

---

## 12) Liability

12.1 Liability under this DPA is subject to the limitations of liability and exclusions in the Terms of Service, except to the extent prohibited by Applicable Data Protection Laws. Nothing in the Agreement limits or excludes liability to the extent it cannot be limited or excluded under Applicable Law, including any statutory rights of data subjects.

---

## 13) Term and termination

13.1 This DPA remains in effect for the duration of EasyAppIcon's processing of Customer Personal Data on behalf of Customer under the Terms of Service.

---

## 14) Contact

Privacy & legal contact: **supports@easyappicon.com**

---

# Annex 1 — Processing Details

**A. Subject matter of processing**
Providing the Services, including icon generation and delivery of generated outputs.

**B. Duration**
For the term of the Customer's use of the Services plus any retention periods described in the Privacy Policy and/or Customer configuration, and any required legal retention.

**C. Nature and purpose of processing**
- receiving Customer Content uploads and parameters;
- transforming images to generate icon assets and packaged outputs;
- storing outputs temporarily for delivery;
- operating and securing the Services as required to provide the processing (e.g., abuse prevention and reliability related to processing requests).

**D. Categories of Data Subjects**
- Customer's authorized users;
- Customer's end users (if Customer uploads content relating to them).

**E. Categories of Personal Data**
- Customer Content: images/files and generation parameters (may include personal data if included by Customer);
- Associated technical metadata necessary to provide the processing: request timestamps and API key identifier; and where applicable, IP address and basic device/browser information for abuse prevention.

**F. Special categories of data**
Not intended to be processed. Customer should not upload PHI or other highly sensitive regulated data unless agreed in writing.

**G. Processing operations**
Collection, recording, organization, structuring, storage (temporary), retrieval, use, transformation, disclosure by transmission (for delivery to Customer), deletion, and destruction.

---

# Annex 2 — Security Measures (Technical & Organizational)

EasyAppIcon maintains a security program designed to protect Personal Data. Measures may include:

**Access control**
- least-privilege access; role-based access controls;
- MFA for administrative access;
- logging of administrative actions (where applicable).

**Encryption**
- TLS 1.2+ for data in transit;
- encryption at rest for supported storage systems (e.g., server-side encryption for object storage).

**Operational security**
- vulnerability and patch management practices;
- monitoring/alerting for availability and security signals;
- rate limiting and abuse prevention controls.

**Data minimization**
- short-lived retention of uploaded Customer Content and outputs where feasible;
- automated deletion via lifecycle rules/jobs.

**Resilience**
- backups for critical systems (excluding content designed for transient storage);
- disaster recovery and availability controls commensurate with service tier.

**Vendor management**
- contractual safeguards with Subprocessors;
- periodic review of Subprocessor changes and security posture where appropriate.

---

# Annex 3 — Subprocessors

The following Subprocessors may process Customer Personal Data on behalf of Customer:

| Subprocessor | Service | Purpose | Processing location(s) |
|---|---|---|---|
| Amazon Web Services (AWS) | Hosting / Storage / Compute | Infrastructure hosting, object storage, compute, logging | United States (e.g., us-east-1) |
| Supabase | Auth / Database | Authentication and database storage for service account data and related service metadata | Singapore |
| Amazon Simple Email Service (SES) | Email | Sending transactional service emails/notifications (e.g., delivery links, account notices) | United States (e.g., us-east-1) |

**For avoidance of doubt:** Stripe (payments/billing) and Google Analytics (website analytics) are used for **Controller Data** purposes and are governed by the Privacy Policy and Terms of Service, not this DPA.

EasyAppIcon may update this list in accordance with Section 6.
`

const DPA = () => {
  return (
    <LegalPageLayout
      title="Data Processing Addendum"
      lastUpdated="January 20, 2026"
      metaDescription="EasyAppIcon Data Processing Addendum (DPA) - Learn about our data processing obligations, security measures, and international transfer mechanisms."
      content={content}
    />
  )
}

export default DPA
